import { useCallback, useRef, useState } from "react";
import { useAlert } from "hooks/useAlert";
import { useConfirmation } from "hooks/useConfirmation";
import { api } from "services/api.service";
import { apiFieldError, isAxiosError } from "utils/helpers";

// Clears the Blancco duplicate-serial flag. A warehouse or state warning comes back as a skip the
// tech can confirm, the same way the rest of an inventory save does. The caller updates its own
// form; this does not replace the inventory the form was editing.
export function useClearSerialVerification(inventoryId: number | undefined, onCleared: () => void) {
  const { alertError, alertSuccess } = useAlert();
  const { confirm } = useConfirmation();
  const [confirming, setConfirming] = useState(false);
  const onClearedRef = useRef(onCleared);
  const confirmingRef = useRef(false);
  const clearRef = useRef<(skipActions?: string[]) => Promise<void>>(async () => {});
  onClearedRef.current = onCleared;

  const clear = useCallback(
    async (skipActions: string[] = []) => {
      if (!inventoryId || confirmingRef.current) return;
      confirmingRef.current = true;
      setConfirming(true);
      try {
        await api.put(`/inventories/${inventoryId}`, {
          inventory: {
            serialVerificationRequired: false,
            ...Object.fromEntries(skipActions.map((flag) => [flag, true])),
          },
        });
        onClearedRef.current();
        alertSuccess("Serial marked as checked.");
      } catch (error) {
        const skipParam = isAxiosError(error) ? apiFieldError(error, "skipParam") : undefined;
        if (isAxiosError(error) && error.response?.status === 422 && skipParam) {
          confirm({
            handler: () => clearRef.current([...skipActions, skipParam]),
            message: apiFieldError(error, "message"),
            context: skipParam,
          });
          return;
        }
        alertError("Couldn't mark the serial as checked.");
      } finally {
        confirmingRef.current = false;
        setConfirming(false);
      }
    },
    [alertError, alertSuccess, confirm, inventoryId],
  );
  clearRef.current = clear;

  return { clear, confirming };
}
